import { BookmarkPaginationSettings } from "constants/bookmark-pagination-constants";
import { MyLiNKQueryKeys } from "constants/my-link-query-keys";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import { useInfiniteQuery } from "react-query";
import { t } from "utilities/localization-utils";
import UserBookmarkService, {
    UserBookmarkListQueryParams,
} from "utilities/services/user-bookmarks/user-bookmark-service";
import { ToastManager } from "utilities/toast/toast-manager";

const DEFAULT_TAKE_SIZE = BookmarkPaginationSettings.AccordionTake;

interface UseBookmarksInPanelQueryArgs {
    groupId: UserBookmarkCountBy;
    id: string | number;
    options: UserBookmarkListQueryParams;
    isOpen: boolean;
}

const useBookmarksInPanelQuery = ({
    groupId,
    id,
    options,
    isOpen,
}: UseBookmarksInPanelQueryArgs) => {
    const querykey = `${MyLiNKQueryKeys.Bookmarks}_${groupId}_${id}`;

    const {
        data: bookmarks,
        isLoading,
        fetchNextPage,
        hasNextPage,
        hasPreviousPage,
    } = useInfiniteQuery(
        [querykey, options.currentUserOnly, options.sortBy, options.searchText],
        {
            queryFn: async ({ pageParam = 0 }) =>
                await fetchBookmarks(groupId, id, pageParam, options),
            getNextPageParam: (lastPage, allPages) => {
                if (
                    lastPage?.rowCount &&
                    lastPage.rowCount <=
                        allPages.length * (options.take ?? DEFAULT_TAKE_SIZE)
                ) {
                    return null;
                } else {
                    return (
                        allPages.length * (options.take ?? DEFAULT_TAKE_SIZE)
                    );
                }
            },
            enabled: isOpen,
        }
    );

    return {
        bookmarks,
        isLoading,
        fetchNextPage,
        hasNextPage,
        hasPreviousPage,
    };
};

const fetchBookmarks = async (
    groupId,
    id,
    pageParam,
    { sortBy, searchText, currentUserOnly }: UserBookmarkListQueryParams
) => {
    try {
        const result = await UserBookmarkService.list({
            collectionId:
                groupId === UserBookmarkCountBy.Collection ? id : undefined,
            code: groupId === UserBookmarkCountBy.Publication ? id : undefined,
            colors: groupId === UserBookmarkCountBy.Color ? [id] : undefined,
            skip: pageParam,
            take: DEFAULT_TAKE_SIZE,
            sortBy,
            searchText,
            currentUserOnly,
        });

        if (result.result?.hasErrors()) {
            throw new Error();
        }
        return {
            bookmarks: result.resultObjects,
            rowCount: result.rowCount,
        };
    } catch (err) {
        ToastManager.error(
            t("errors-actionResource", {
                action: t("loading"),
                resource: t("bookmark_plural"),
            })
        );
    }
};

export default useBookmarksInPanelQuery;
